import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  Output,
  TemplateRef
} from '@angular/core';
import { ItemBoxStateDirective } from './item-box-state.directive';

const transitionDuration = 150;

@Component({
  selector: 'app-item-box',
  templateUrl: './item-box.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('parentAnimation', [transition(':enter', [])]),
    // To prevent initial animation
    // https://stackoverflow.com/questions/44111239/angular-is-there-a-way-to-skip-enter-animation-on-initial-render
    trigger('expandCollapse', [
      state('expandCollapseState', style({ height: '*' })),
      transition('* => void', [style({ height: '*' }), animate(transitionDuration, style({ height: '0' }))]),
      transition('void => *', [style({ height: '0' }), animate(transitionDuration, style({ height: '*' }))]),
    ]),
  ],
})
export class ItemBoxComponent implements OnInit {
  private isExpanded: boolean;

  // Allows auto-tests to understand if the component is either expanded or collapsed and animation is done
  @HostBinding('attr.expansion-state')
  private expandState: 'expanded' | 'collapsed' = 'collapsed';

  private get isExpandable(): boolean {
    return this.bodyTemplate && this.expandable;
  }

  protected get hasCursorPointer(): boolean {
    return this.isExpandable || this.hoverable;
  }

  @ContentChild('header')
  protected headerTemplate: TemplateRef<any>;

  @ContentChild('body')
  protected bodyTemplate: TemplateRef<any>;

  @Input()
  itemId: string;

  @Input()
  hoverable: boolean;

  @Input()
  backgroundType: 'light' | 'dark' = 'light';

  @Input()
  shadow = true;

  @Input()
  expandable: boolean;

  @Input()
  disabled: boolean;

  @Input()
  focused: boolean;

  @HostBinding('class.border-navy-80')
  @HostBinding('class.shadow-sm')
  get focusedClass(): boolean {
    return this.focused;
  }

  get expanded(): boolean {
    return this.isExpanded;
  }

  @Input()
  set expanded(value: boolean) {
    this.isExpanded = value;
  }

  @Input()
  headerType: 'normal' | 'compact' = 'normal';

  @Input()
  disableToggle = false;

  @Output()
  headerClick = new EventEmitter<MouseEvent>();

  @Output()
  afterExpand = new EventEmitter<any>();

  @Output()
  afterCollapse = new EventEmitter<any>();

  @Output()
  expandedChange = new EventEmitter<boolean>();

  constructor(private cd: ChangeDetectorRef, private elementRef: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    if (this.getItemStateInstance()) {
      this.isExpanded = this.getItemStateInstance().isExpanded(this.itemId);
    }
  }

  open(): void {
    if (this.isExpandable) {
      this.isExpanded = true;
      this.cd.detectChanges();
      this.expandedChange.emit(this.isExpanded);
    }
  }

  close(): void {
    if (this.isExpandable) {
      this.isExpanded = false;
      this.cd.detectChanges();
      this.expandedChange.emit(this.isExpanded);
    }
  }

  toggle(): void {
    if (this.disableToggle) {
      return;
    }

    if (this.isExpanded) {
      this.close();
    } else {
      this.open();
    }
  }

  protected handleHeaderClick(event: MouseEvent): void {
    this.headerClick.emit(event);
    this.toggle();
  }

  protected handleAfterExpand(): void {
    this.getItemStateInstance()?.setExpanded(this.itemId);
  }

  protected handleAfterCollapse(): void {
    this.getItemStateInstance()?.setCollapsed(this.itemId);
  }

  protected animationDone(): void {
    if (this.isExpanded) {
      this.handleAfterExpand();
      this.afterExpand.emit();
      this.expandState = 'expanded';
      this.cd.markForCheck();
    } else {
      this.handleAfterCollapse();
      this.afterCollapse.emit();
      this.expandState = 'collapsed';
      this.cd.markForCheck();
    }
  }

  @HostListener('click', ['$event'])
  private onClick(event: MouseEvent): void {
    if (this.disabled) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  // With this trick we find ItemBoxStateDirective instance by going up by the tree
  // This gives us the ability just put ItemBoxStateDirective onto wrapper element
  // and that will ensure item box state is properly saved
  private getItemStateInstance(): ItemBoxStateDirective {
    const itemStateInitializedKey = 'itemStateInitialized';
    const itemStateKey = 'itemState';
    const itemStateInstanceKey = 'itemBoxStateInstance';

    if (!this.elementRef?.nativeElement) {
      return;
    }

    if (!this[itemStateInitializedKey]) {
      // eslint-disable-next-line no-inner-declarations
      function findItemBoxState(element: HTMLElement): HTMLElement {
        if (!element || !element.parentElement) {
          return;
        }

        if (element[itemStateInstanceKey] instanceof ItemBoxStateDirective) {
          return element;
        }

        return findItemBoxState(element.parentElement);
      }

      const itemStateElement = findItemBoxState(this.elementRef.nativeElement);

      if (itemStateElement && itemStateElement[itemStateInstanceKey]) {
        this[itemStateKey] = itemStateElement[itemStateInstanceKey];
      }

      this[itemStateInitializedKey] = true;
    }

    return this[itemStateKey];
  }
}
